import type { ArticleChecks, ArticleDoc, ArticleLinks, ArticleSection } from '../../types/content'
import { articleBlockers } from '../../lib/content/article-inspector/blockers'

/** The markdown of one section's text with its key facts, as the document and the CMS show it. */
export function sectionMarkdown(section: Pick<ArticleSection, 'text' | 'keyFacts'>): string {
  if (section.keyFacts.length === 0) return section.text
  return `${section.text}\n\n**Key facts**\n\n${section.keyFacts.map((fact) => `- ${fact}`).join('\n')}`
}

const UNRESOLVED_LINK = /\[([^\]]+)\]\([^)\s/:.#]+\)/g

function unlinked(text: string): string {
  return text.replace(UNRESOLVED_LINK, '$1')
}

/** The answer and sections the document shows: the resolved text once read, else the stored text with its unresolved links as plain words. */
export function documentText(
  article: Pick<ArticleDoc, 'answer' | 'sections'>,
  linked: Pick<ArticleLinks, 'answer' | 'sections'> | null,
): { answer: string; sections: ArticleSection[] } {
  const resolved = new Map(linked?.sections.map((section) => [section.id, section.text]))
  return {
    answer: linked?.answer ?? unlinked(article.answer),
    sections: article.sections.map((section) => ({
      ...section,
      text: resolved.get(section.id) ?? unlinked(section.text),
      keyFacts: section.keyFacts.map(unlinked),
    })),
  }
}

export interface TodoItem {
  id: string
  /** The sentence the check could not support, or the rule the lint broke. */
  title: string
  /** Why it is open; empty for a lint finding. */
  why: string
  sectionId: string | null
}

/** What the founder still has to settle: the check's unsupported sentences, then the hard lint findings. */
export function todoItems(checks: ArticleChecks | null): TodoItem[] {
  if (!checks) return []
  const findings: TodoItem[] = checks.findings.map((finding, index) => ({
    id: `finding-${index}`,
    title: finding.sentence,
    why: finding.why,
    sectionId: finding.section,
  }))
  const lint: TodoItem[] = checks.lint
    .filter((finding) => finding.severity === 'hard')
    .map((finding, index) => ({
      id: `lint-${finding.rule}-${index}`,
      title: finding.message,
      why: '',
      sectionId: null,
    }))
  return [...findings, ...lint]
}

/** How many rows the Details view lists under "Before you publish". */
export function articleTodoCount(article: Pick<ArticleDoc, 'checks' | 'handoff'>): number {
  return articleBlockers(article).length
}

export const RESULT_TITLE_MAX_CHARS = 60
export const RESULT_SUMMARY_MAX_CHARS = 155

const RESULT_CUT_WORD_BREAK_SHARE = 0.6

/** Splits `text` where a search result drops it past `max` characters: at the last space when that space is past 60% of `max`, else hard at `max`. */
export function resultCut(text: string, max: number): { kept: string; cut: string } {
  if (text.length <= max) return { kept: text, cut: '' }
  const space = text.lastIndexOf(' ', max)
  const at = space > max * RESULT_CUT_WORD_BREAK_SHARE ? space : max
  return { kept: text.slice(0, at), cut: text.slice(at) }
}
